import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { 
  Users, ChevronLeft, Home, Check, AlertCircle, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

const AgentApplicationPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    license_number: '',
    agency_name: '',
    years_experience: '',
    specializations: [],
    bio: '',
    terms_accepted: false
  });

  const specializations = [
    'Luxury Properties',
    'Off-Plan',
    'Commercial',
    'Residential',
    'Investment Properties',
    'Waterfront',
    'New Developments'
  ];

  const handleSpecializationToggle = (spec) => {
    setFormData(prev => ({
      ...prev,
      specializations: prev.specializations.includes(spec)
        ? prev.specializations.filter(s => s !== spec)
        : [...prev.specializations, spec]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.terms_accepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }
    
    if (!formData.license_number || !formData.agency_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/agent/apply?user_id=${user?.id}`, {
        license_number: formData.license_number,
        agency_name: formData.agency_name,
        years_experience: parseInt(formData.years_experience) || 0,
        specializations: formData.specializations,
        bio: formData.bio
      });
      
      toast.success('Application submitted successfully! You will be notified once approved.');
      navigate('/agent/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="agent-application-page">
      {/* Header */}
      <header className="border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <Home className="w-4 h-4 mr-1" />
              Home
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded logo-icon-metallic flex items-center justify-center">
                <span className="font-display text-sm text-primary-foreground font-bold">R</span>
              </div>
              <span className="font-display text-xl">Agent Application</span>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12 max-w-2xl">
        <Card className="border-white/10">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-gold-600/20 flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-gold-400" />
            </div>
            <CardTitle className="font-display text-2xl">Become a Rashii Agent</CardTitle>
            <CardDescription>
              Join our network of verified real estate professionals
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* License Number */}
              <div>
                <Label htmlFor="license_number">RERA License Number *</Label>
                <Input
                  id="license_number"
                  placeholder="e.g., DLD-2024-12345"
                  value={formData.license_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, license_number: e.target.value }))}
                  className="mt-2 bg-white/5 border-white/10"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Your Dubai Land Department real estate license number
                </p>
              </div>

              {/* Agency Name */}
              <div>
                <Label htmlFor="agency_name">Agency / Brokerage Name *</Label>
                <Input
                  id="agency_name"
                  placeholder="e.g., Subarashi Real Estate"
                  value={formData.agency_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, agency_name: e.target.value }))}
                  className="mt-2 bg-white/5 border-white/10"
                  required
                />
              </div>

              {/* Years of Experience */}
              <div>
                <Label htmlFor="years_experience">Years of Experience</Label>
                <Input
                  id="years_experience"
                  type="number"
                  min="0"
                  max="50"
                  placeholder="e.g., 5"
                  value={formData.years_experience}
                  onChange={(e) => setFormData(prev => ({ ...prev, years_experience: e.target.value }))}
                  className="mt-2 bg-white/5 border-white/10"
                />
              </div>

              {/* Specializations */}
              <div>
                <Label>Specializations</Label>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  {specializations.map((spec) => (
                    <div
                      key={spec}
                      className={`
                        p-3 rounded-lg border cursor-pointer transition-all
                        ${formData.specializations.includes(spec)
                          ? 'border-gold-600 bg-gold-600/10'
                          : 'border-white/10 hover:border-white/30'
                        }
                      `}
                      onClick={() => handleSpecializationToggle(spec)}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded border flex items-center justify-center
                          ${formData.specializations.includes(spec)
                            ? 'bg-gold-600 border-gold-600'
                            : 'border-white/30'
                          }
                        `}>
                          {formData.specializations.includes(spec) && (
                            <Check className="w-3 h-3 text-primary-foreground" />
                          )}
                        </div>
                        <span className="text-sm">{spec}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bio */}
              <div>
                <Label htmlFor="bio">Professional Bio (Optional)</Label>
                <textarea
                  id="bio"
                  placeholder="Tell us about your experience and expertise..."
                  value={formData.bio}
                  onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                  className="mt-2 w-full h-24 px-3 py-2 rounded-md bg-white/5 border border-white/10 focus:border-gold-600 focus:outline-none resize-none"
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {formData.bio.length}/500 characters
                </p>
              </div>

              {/* Terms */}
              <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms"
                    checked={formData.terms_accepted}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, terms_accepted: checked }))}
                  />
                  <div>
                    <Label htmlFor="terms" className="text-sm cursor-pointer">
                      I agree to the Rashii Agent Terms & Conditions
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      By applying, you confirm that you hold a valid RERA license and agree to operate within Rashii's governance framework.
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full btn-gold-metallic"
                disabled={submitting || !formData.terms_accepted}
              >
                {submitting ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                ) : (
                  'Submit Application'
                )}
              </Button>
            </form>

            {/* Info Box */}
            <div className="mt-6 p-4 rounded-lg bg-gold-950/20 border border-gold-600/30">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-gold-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gold-400">What happens next?</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Our team will review your application within 24-48 hours. Once approved, you'll receive an email notification and gain access to the Agent Portal.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AgentApplicationPage;
